import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      // The endpoint is anti-enumeration and normally always succeeds; a 429
      // (rate limit) is the main case we surface.
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll send you a link to set a new password."
      footer={
        <>
          Remembered it?{" "}
          <Link to="/login" data-testid="back-to-login" className="text-klein font-semibold hover:underline">
            Back to sign in
          </Link>
        </>
      }
    >
      {sent ? (
        <div className="text-center py-4" data-testid="forgot-sent">
          <div className="w-12 h-12 rounded-full bg-good/10 flex items-center justify-center mx-auto">
            <MailCheck className="w-6 h-6 text-good" />
          </div>
          <h3 className="font-heading text-lg font-bold mt-4">Check your email</h3>
          <p className="text-gray-500 text-sm mt-1">
            If an account exists for <span className="font-medium text-gray-700">{email}</span>, a reset link is on its way. It expires in 30 minutes.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="forgot-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5"
              placeholder="you@company.com"
            />
          </div>
          {error && <p data-testid="forgot-error" className="text-sm text-bad font-medium">{error}</p>}
          <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full bg-[#0A0A0A] hover:bg-gray-800 py-6 text-base">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
